import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Bot,
  Check,
  CheckCircle,
  Copy,
  FileAudio,
  Loader2,
  Mic,
  RefreshCw,
  Send,
  Sparkles,
  Square,
  Star,
  Trash2,
  Upload,
  Video,
  XCircle,
} from "lucide-react";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

import { usePolling } from "@/hooks/usePolling";
import {
  cancelTestBot,
  createTestBot,
  getTestBot,
  synthesizeTest,
  transcribeTest,
  type BotMappedStatus,
  type BotTestResult,
  type BotTestStatus,
  type InterviewSynthesis,
  type SynthesizeTestResult,
  type TranscribeTestResult,
  type TranscriptEntity,
} from "@/services/interviewToolsApi";

const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;
// Mirrors the caps enforced by back/'s SynthesizeTestRequest — clamped in the
// textarea so an over-long paste is visibly truncated instead of 422-ing after
// the round trip. 15 000 is also where the synthesis prompt itself truncates.
const MAX_TRANSCRIPT_CHARS = 15000;
const MAX_CONTEXT_CHARS = 4000;

// Terminal `mapped_status` values — once reached, the bot run is over and
// polling stops. Anything else (including `null`/unknown) is treated as
// still in flight so we keep polling rather than risk missing a transition.
const BOT_TERMINAL_STATUSES = new Set<BotMappedStatus>([
  "completed",
  "denied_entry",
  "no_show",
  "failed",
  "too_short",
  "cancelled",
]);

// French labels for the raw `mapped_status` vocabulary — mirrors the wording
// used by the recruiter UI (front/src/i18n/locales/fr/booking.json `bot.status.*`
// via front/src/components/interview/botStatus.ts), adapted to the raw
// (non-grouped) statuses this admin-only endpoint returns.
const BOT_STATUS_LABELS: Record<BotMappedStatus, string> = {
  scheduled: "Bot programmé",
  joining: "En attente d'admission",
  waiting_room: "En attente d'admission",
  in_call: "Enregistrement en cours",
  post_processing: "Traitement en cours",
  media_ready: "Traitement en cours",
  completed: "Enregistré",
  denied_entry: "Bot non admis",
  no_show: "Entretien non tenu",
  failed: "Échec du bot",
  too_short: "Enregistrement trop court",
  cancelled: "Bot annulé",
};

// Soft color accents per status, same palette style as front/'s BotStatusBadge.
// Statuses absent here (failed / cancelled) fall back to destructive/secondary.
const BOT_STATUS_BADGE_CLASS: Partial<Record<BotMappedStatus, string>> = {
  scheduled: "bg-blue-100 text-blue-800 hover:bg-blue-200 border-transparent",
  joining: "bg-blue-100 text-blue-800 hover:bg-blue-200 border-transparent",
  waiting_room: "bg-blue-100 text-blue-800 hover:bg-blue-200 border-transparent",
  in_call: "bg-red-100 text-red-800 hover:bg-red-200 border-transparent",
  post_processing: "bg-violet-100 text-violet-800 hover:bg-violet-200 border-transparent",
  media_ready: "bg-violet-100 text-violet-800 hover:bg-violet-200 border-transparent",
  completed: "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border-transparent",
  denied_entry: "bg-amber-100 text-amber-800 hover:bg-amber-200 border-transparent",
  no_show: "bg-amber-100 text-amber-800 hover:bg-amber-200 border-transparent",
  too_short: "bg-amber-100 text-amber-800 hover:bg-amber-200 border-transparent",
};

function BotStatusBadge({ status }: { status: BotMappedStatus | null }) {
  if (!status) return <Badge variant="outline">Statut inconnu</Badge>;
  const label = BOT_STATUS_LABELS[status];
  if (status === "failed") return <Badge variant="destructive">{label}</Badge>;
  const colorClass = BOT_STATUS_BADGE_CLASS[status];
  if (!colorClass) return <Badge variant="secondary">{label}</Badge>;
  return <Badge className={colorClass}>{label}</Badge>;
}

// Color accents per NER entity label (RA-007.1). PERSON/ORG come from
// spaCy's statistical NER, SKILL from a gazetteer matched against known
// skill names — see back/src/features/interview/ner_service.py. Admin keeps
// its own copy rather than importing from front/, same convention as
// BotStatusBadge above.
const ENTITY_LABEL_CLASS: Record<TranscriptEntity["label"], string> = {
  PERSON: "bg-blue-200 text-blue-900",
  ORG: "bg-amber-200 text-amber-900",
  SKILL: "bg-emerald-200 text-emerald-900",
};

/** Splits `text` into plain/highlighted runs at each entity's [start, end). */
function renderTextWithEntities(text: string, entities: TranscriptEntity[]) {
  if (entities.length === 0) return text;
  const sorted = [...entities].sort((a, b) => a.start - b.start);
  const parts: ReactNode[] = [];
  let cursor = 0;
  sorted.forEach((ent, i) => {
    if (ent.start > cursor) parts.push(text.slice(cursor, ent.start));
    parts.push(
      <mark key={i} className={`rounded px-0.5 ${ENTITY_LABEL_CLASS[ent.label]}`} title={ent.label}>
        {text.slice(ent.start, ent.end)}
      </mark>,
    );
    cursor = ent.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatElapsed(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
  const s = Math.floor(totalSeconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

// Candidate mimeTypes in preference order — all accepted by the transcribe
// endpoint (mp3, wav, m4a, webm, ogg, mp4). Chrome/Edge support webm+opus;
// Safari typically only supports mp4.
const RECORDER_MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

function pickRecorderMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return undefined;
  return RECORDER_MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

function extensionForMimeType(mimeType: string): string {
  if (mimeType.includes("mp4")) return "mp4";
  if (mimeType.includes("ogg")) return "ogg";
  return "webm";
}

/**
 * Records audio from the microphone via the browser MediaRecorder API (no
 * npm dependency). `onRecorded` fires once with the finished File when the
 * user stops recording.
 */
function useAudioRecorder(onRecorded: (file: File) => void) {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [recorderError, setRecorderError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const start = useCallback(async () => {
    setRecorderError(null);
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setRecorderError("L'enregistrement micro n'est pas disponible dans ce navigateur.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickRecorderMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        chunksRef.current = [];
        const file = new File(
          [blob],
          `enregistrement-${Date.now()}.${extensionForMimeType(recorder.mimeType)}`,
          { type: blob.type },
        );
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        onRecorded(file);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    } catch (e) {
      setRecorderError(e instanceof Error ? e.message : "Impossible d'accéder au micro.");
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, [onRecorded]);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
    mediaRecorderRef.current?.stop();
  }, []);

  // Unmount safety net: release the mic and detach handlers so a late
  // `stop` event can't call back into state setters after the page is gone.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.ondataavailable = null;
        mediaRecorderRef.current.onstop = null;
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return { isRecording, elapsedSeconds, recorderError, start, stop };
}

function ratingColor(rating: number): string {
  if (rating >= 7) return "text-green-600";
  if (rating >= 5) return "text-yellow-600";
  return "text-red-600";
}

/**
 * Renders a structured interview synthesis. Shared by the two tabs that can
 * produce one (audio -> transcript -> synthesis, and transcript -> synthesis)
 * so the two never drift into showing different fields for the same payload.
 */
function SynthesisView({ synthesis }: { synthesis: InterviewSynthesis }) {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <Sparkles className="h-4 w-4" /> Synthèse IA
      </h3>

      <div className="flex items-center gap-3 bg-muted/40 rounded-lg p-3">
        <Star className="h-5 w-5 text-yellow-500" fill="currentColor" />
        <span className={`text-2xl font-bold ${ratingColor(synthesis.overall_rating)}`}>
          {synthesis.overall_rating}/10
        </span>
        <span className="text-sm text-muted-foreground">Note globale</span>
      </div>

      <div>
        <h4 className="text-sm font-medium mb-1">Résumé</h4>
        <p className="text-sm text-muted-foreground">{synthesis.summary}</p>
      </div>

      {synthesis.key_points?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-1">Points clés</h4>
          <ul className="space-y-1">
            {synthesis.key_points.map((point, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <ArrowRight className="h-3 w-3 mt-1 text-blue-500 shrink-0" />
                {point}
              </li>
            ))}
          </ul>
        </div>
      )}

      {synthesis.candidate_assessment?.strengths?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-1 flex items-center gap-1 text-green-800">
            <CheckCircle className="h-3.5 w-3.5" /> Forces
          </h4>
          <ul className="space-y-1">
            {synthesis.candidate_assessment.strengths.map((s, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-green-700">
                <CheckCircle className="h-3 w-3 mt-1 shrink-0" />
                {s}
              </li>
            ))}
          </ul>
        </div>
      )}

      {synthesis.candidate_assessment?.concerns?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-1 flex items-center gap-1 text-yellow-800">
            <AlertTriangle className="h-3.5 w-3.5" /> Points d'attention
          </h4>
          <ul className="space-y-1">
            {synthesis.candidate_assessment.concerns.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-yellow-700">
                <AlertTriangle className="h-3 w-3 mt-1 shrink-0" />
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}

      {synthesis.red_flags?.length > 0 && (
        <div className="bg-red-50 rounded-lg p-3">
          <h4 className="text-sm font-medium mb-1 flex items-center gap-1 text-red-800">
            <AlertCircle className="h-3.5 w-3.5" /> Alertes
          </h4>
          <ul className="space-y-1">
            {synthesis.red_flags.map((flag, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                <AlertCircle className="h-3 w-3 mt-1 shrink-0" />
                {flag}
              </li>
            ))}
          </ul>
        </div>
      )}

      {synthesis.next_steps?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-1">Prochaines étapes</h4>
          <ul className="space-y-1">
            {synthesis.next_steps.map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                <ArrowRight className="h-3 w-3 mt-1 text-purple-500 shrink-0" />
                {step}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function InterviewToolsPage() {
  const navigate = useNavigate();

  // --- Transcription tab --------------------------------------------------
  const [file, setFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [synthesize, setSynthesize] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState<string | null>(null);
  const [result, setResult] = useState<TranscribeTestResult | null>(null);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const applyFile = useCallback((f: File) => {
    setResult(null);
    setTranscribeError(null);
    if (f.size > MAX_UPLOAD_BYTES) {
      setTranscribeError(`Fichier trop volumineux (${formatBytes(f.size)}) — maximum 100 Mo.`);
      setFile(null);
      return;
    }
    setFile(f);
  }, []);

  const { isRecording, elapsedSeconds, recorderError, start: startRecording, stop: stopRecording } =
    useAudioRecorder(applyFile);

  // Playback URL for whichever file is currently selected (upload or
  // recording). Created/revoked strictly in an effect (not useMemo) so the
  // object URL lifecycle stays correct even under React 18 double-invoke.
  useEffect(() => {
    if (!file) {
      setAudioUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleStartRecording = () => {
    setFile(null);
    setResult(null);
    setTranscribeError(null);
    void startRecording();
  };

  const handleClearFile = () => {
    setFile(null);
    setResult(null);
    setTranscribeError(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    if (chosen) applyFile(chosen);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (isRecording) return;
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) applyFile(dropped);
  };

  const handleTranscribe = async () => {
    if (!file || transcribing) return;
    setTranscribing(true);
    setTranscribeError(null);
    try {
      const data = await transcribeTest(navigate, file, synthesize);
      setResult(data);
    } catch (e) {
      setTranscribeError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setTranscribing(false);
    }
  };

  const handleCopyTranscript = async () => {
    if (!result?.transcript) return;
    try {
      await navigator.clipboard.writeText(result.transcript);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable/denied — non-blocking, no-op.
    }
  };

  // --- Synthesis-only tab --------------------------------------------------
  // Deliberately separate from the transcription tab's state: the point of this
  // tab is to exercise the LLM leg ALONE, so nothing here may depend on a file
  // having been transcribed first.
  const [synthTranscript, setSynthTranscript] = useState("");
  const [synthContext, setSynthContext] = useState("");
  const [synthesizing, setSynthesizing] = useState(false);
  const [synthError, setSynthError] = useState<string | null>(null);
  const [synthResult, setSynthResult] = useState<SynthesizeTestResult | null>(null);

  const handleSynthesize = async () => {
    const transcript = synthTranscript.trim();
    if (!transcript || synthesizing) return;
    setSynthesizing(true);
    setSynthError(null);
    try {
      const data = await synthesizeTest(navigate, transcript, synthContext.trim() || null);
      setSynthResult(data);
    } catch (e) {
      setSynthResult(null);
      setSynthError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setSynthesizing(false);
    }
  };

  // --- Meeting bot tab -----------------------------------------------------
  const [meetingUrl, setMeetingUrl] = useState("");
  const [botName, setBotName] = useState("");
  // RA-007.1: exercises the SKILL entity matcher without a real Candidate
  // (the sandbox has no candidate to pull skills from — see ner_service.py).
  const [testSkills, setTestSkills] = useState("");
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  // Survives a page refresh (F5) — otherwise a reload wipes this in-memory
  // state and the sandbox "forgets" the bot mid-test (the bot itself, and
  // its transcript, are still live server-side; only the UI's pointer to it
  // was lost). Session-scoped: cleared with the tab, not shared across
  // browsers — a bot_id has no sensitive content, just an opaque test id.
  const BOT_STORAGE_KEY = "recrutauto-admin-interview-tools-bot";
  const [bot, setBot] = useState<BotTestResult | null>(() => {
    try {
      const raw = sessionStorage.getItem(BOT_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as BotTestResult) : null;
    } catch {
      return null;
    }
  });
  const [botStatus, setBotStatus] = useState<BotTestStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [manuallyCancelled, setManuallyCancelled] = useState(false);

  // Keep sessionStorage in sync with `bot` — covers launch, reset, and the
  // initial-mount case in one place instead of duplicating the write at
  // every call site that changes `bot`.
  useEffect(() => {
    try {
      if (bot) sessionStorage.setItem(BOT_STORAGE_KEY, JSON.stringify(bot));
      else sessionStorage.removeItem(BOT_STORAGE_KEY);
    } catch {
      // Storage unavailable (private browsing, quota) — non-blocking.
    }
  }, [bot]);

  const mappedStatus: BotMappedStatus | null = manuallyCancelled
    ? "cancelled"
    : botStatus?.mapped_status ?? null;
  const isTerminal = mappedStatus !== null && BOT_TERMINAL_STATUSES.has(mappedStatus);
  const pollingEnabled = !!bot && !isTerminal;

  const fetchBotStatus = useCallback(async () => {
    if (!bot) return;
    try {
      const s = await getTestBot(navigate, bot.bot_id, testSkills.trim() || undefined);
      setBotStatus(s);
      setStatusError(null);
    } catch (e) {
      setStatusError(e instanceof Error ? e.message : "Erreur inconnue");
    }
  }, [bot, navigate, testSkills]);

  // 2s rather than 5s — the real latency floor is Echo's VAD-based utterance
  // closing (an utterance only finalizes after ~2s of silence or a 25s cap,
  // see local-whisper's asr-bridge design notes), not this poll interval;
  // polling faster helps utterances *display* sooner once Attendee has them,
  // but can't make Echo close an utterance any earlier.
  usePolling(fetchBotStatus, 2000, { enabled: pollingEnabled });

  const handleLaunchBot = async () => {
    const url = meetingUrl.trim();
    if (!url || launching) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      const created = await createTestBot(navigate, url, botName.trim() || null);
      setBot(created);
      setBotStatus(null);
      setStatusError(null);
      setCancelError(null);
      setManuallyCancelled(false);
    } catch (e) {
      setLaunchError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLaunching(false);
    }
  };

  const handleCancelBot = async () => {
    if (!bot || cancelling) return;
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelTestBot(navigate, bot.bot_id);
      setManuallyCancelled(true);
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setCancelling(false);
    }
  };

  const handleResetBot = () => {
    setBot(null);
    setBotStatus(null);
    setManuallyCancelled(false);
    setLaunchError(null);
    setStatusError(null);
    setCancelError(null);
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Outils entretien IA</h1>
          </div>
        </header>

        <div className="p-6 space-y-6 max-w-4xl">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <Mic className="h-6 w-6" />
              Outils entretien IA
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Bac à sable interne pour tester la transcription (Whisper), la synthèse IA
              d'entretien et le bot de réunion, sans passer par une vraie campagne ni un vrai
              candidat. Rien n'est sauvegardé côté RecrutAuto en dehors du log d'audit (taille du
              fichier / URL testée, jamais le contenu).{" "}
              <strong>Quand ça casse, commencez par « Synthèse seule »</strong> : cet onglet
              n'appelle que le LLM, donc il sépare une panne de la passerelle d'une panne de
              Whisper — que l'onglet Transcription, qui enchaîne les deux, ne peut pas distinguer.
            </p>
          </div>

          <Tabs defaultValue="transcription">
            <TabsList>
              <TabsTrigger value="transcription" className="gap-1.5">
                <FileAudio className="h-4 w-4" /> Transcription
              </TabsTrigger>
              <TabsTrigger value="synthese" className="gap-1.5">
                <Sparkles className="h-4 w-4" /> Synthèse seule
              </TabsTrigger>
              <TabsTrigger value="bot" className="gap-1.5">
                <Video className="h-4 w-4" /> Bot de réunion
              </TabsTrigger>
            </TabsList>

            {/* --- Transcription --- */}
            <TabsContent value="transcription" className="mt-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Fichier audio</CardTitle>
                  <CardDescription>
                    Déposez un fichier (mp3, wav, m4a, webm, ogg, mp4 — 100 Mo max) ou enregistrez
                    directement depuis le micro.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Choisir un fichier audio"
                    onClick={() => !isRecording && fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if ((e.key === "Enter" || e.key === " ") && !isRecording) {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (!isRecording) setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                      isRecording
                        ? "cursor-not-allowed opacity-50 border-muted-foreground/25"
                        : "cursor-pointer " +
                          (isDragging
                            ? "border-primary bg-primary/5"
                            : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/40")
                    }`}
                  >
                    <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Cliquez ou déposez un fichier audio ici
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp3,.wav,.m4a,.webm,.ogg,.mp4"
                    className="hidden"
                    onChange={handleFileInputChange}
                  />

                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="h-px flex-1 bg-border" />
                    ou
                    <span className="h-px flex-1 bg-border" />
                  </div>

                  <div className="space-y-2">
                    <Label>Enregistrer depuis le micro</Label>
                    <div className="flex items-center gap-3">
                      {!isRecording ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleStartRecording}
                          disabled={transcribing}
                        >
                          <Mic className="h-4 w-4 mr-2" /> Enregistrer
                        </Button>
                      ) : (
                        <Button type="button" variant="destructive" onClick={stopRecording}>
                          <Square className="h-4 w-4 mr-2" /> Arrêter ({formatElapsed(elapsedSeconds)})
                        </Button>
                      )}
                      {isRecording && (
                        <span className="flex items-center gap-1.5 text-sm text-red-600">
                          <span className="h-2 w-2 rounded-full bg-red-600 animate-pulse" />
                          Enregistrement en cours…
                        </span>
                      )}
                    </div>
                    {recorderError && <p className="text-xs text-destructive">{recorderError}</p>}
                  </div>

                  {file && (
                    <div className="space-y-2 border rounded-md p-3 bg-muted/30">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0 text-sm">
                          <FileAudio className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate font-medium">{file.name}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {formatBytes(file.size)}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleClearFile}
                          disabled={transcribing}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Retirer
                        </Button>
                      </div>
                      {audioUrl && (
                        <audio controls src={audioUrl} className="w-full h-9">
                          Votre navigateur ne supporte pas la lecture audio.
                        </audio>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <Label htmlFor="synthesize-toggle">Générer aussi la synthèse</Label>
                      <p className="text-xs text-muted-foreground">
                        Lance en plus la synthèse IA de l'entretien (résumé, points clés,
                        évaluation) sur le transcript obtenu.
                      </p>
                    </div>
                    <Switch
                      id="synthesize-toggle"
                      checked={synthesize}
                      onCheckedChange={setSynthesize}
                      disabled={transcribing}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <Button onClick={handleTranscribe} disabled={!file || transcribing}>
                      {transcribing ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <FileAudio className="h-4 w-4 mr-2" />
                      )}
                      {transcribing ? "Transcription en cours..." : "Transcrire"}
                    </Button>
                    {transcribing && (
                      <span className="text-xs text-muted-foreground">
                        Peut prendre 30 à 60 secondes selon la durée du fichier.
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>

              {transcribeError && (
                <Card className="border-destructive">
                  <CardContent className="pt-6 text-sm text-destructive">
                    {transcribeError}
                  </CardContent>
                </Card>
              )}

              {result && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Résultat</CardTitle>
                    <CardDescription>
                      {result.filename} · {formatBytes(result.size)} ·{" "}
                      {result.language ? `langue détectée : ${result.language}` : "langue non détectée"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Transcript</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCopyTranscript}
                          disabled={!result.transcript}
                        >
                          {copied ? (
                            <>
                              <Check className="h-3.5 w-3.5 mr-1.5 text-green-600" /> Copié
                            </>
                          ) : (
                            <>
                              <Copy className="h-3.5 w-3.5 mr-1.5" /> Copier
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="max-h-80 overflow-y-auto rounded-md border bg-muted/40 p-3">
                        <p className="whitespace-pre-wrap font-mono text-xs">
                          {result.transcript || "(transcript vide)"}
                        </p>
                      </div>
                    </div>

                    {result.synthesis && (
                      <div className="border-t pt-4">
                        <SynthesisView synthesis={result.synthesis} />
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* --- Synthèse seule --- */}
            <TabsContent value="synthese" className="mt-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Transcript</CardTitle>
                  <CardDescription>
                    Collez un transcript et lancez la synthèse seule. Aucun audio, donc Whisper
                    n'est pas dans la boucle : si ça échoue ici, le problème est du côté LLM, et
                    nulle part ailleurs.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="synth-transcript">
                      Transcript <span className="text-muted-foreground">(15 000 caractères max)</span>
                    </Label>
                    <Textarea
                      id="synth-transcript"
                      value={synthTranscript}
                      onChange={(e) => setSynthTranscript(e.target.value.slice(0, MAX_TRANSCRIPT_CHARS))}
                      placeholder={
                        "Recruteur : Bonjour, pouvez-vous vous présenter ?\n" +
                        "Candidat : Bien sûr, je suis développeur Python depuis cinq ans…"
                      }
                      rows={10}
                      className="font-mono text-xs"
                      disabled={synthesizing}
                    />
                    <p className="text-xs text-muted-foreground">
                      {synthTranscript.length} / {MAX_TRANSCRIPT_CHARS} caractères
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="synth-context">
                      Contexte candidat <span className="text-muted-foreground">(facultatif)</span>
                    </Label>
                    <Textarea
                      id="synth-context"
                      value={synthContext}
                      onChange={(e) => setSynthContext(e.target.value.slice(0, MAX_CONTEXT_CHARS))}
                      placeholder="Profil, poste visé, séniorité… — ce que le pipeline réel injecte depuis la fiche candidat."
                      rows={3}
                      className="text-xs"
                      disabled={synthesizing}
                    />
                  </div>

                  {synthError && (
                    <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>{synthError}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleSynthesize}
                      disabled={!synthTranscript.trim() || synthesizing}
                    >
                      {synthesizing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Synthèse en cours…
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" /> Lancer la synthèse
                        </>
                      )}
                    </Button>
                    {synthTranscript && !synthesizing && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSynthTranscript("");
                          setSynthContext("");
                          setSynthResult(null);
                          setSynthError(null);
                        }}
                      >
                        Effacer
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {synthResult && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Résultat</CardTitle>
                    <CardDescription>Routage effectif de l'appel — c'est l'intérêt de cet onglet.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-2 rounded-md border bg-muted/40 p-3 text-xs sm:grid-cols-3">
                      <div>
                        <span className="text-muted-foreground">Alias appelé</span>
                        <p className="font-mono font-medium break-all">{synthResult.model}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Passerelle LLM</span>
                        {synthResult.base_url ? (
                          <p className="font-mono font-medium break-all text-green-700">
                            {synthResult.base_url}
                          </p>
                        ) : (
                          <p className="font-medium text-red-700">
                            aucune — appel direct au fournisseur
                          </p>
                        )}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Durée</span>
                        <p className="font-medium">{(synthResult.elapsed_ms / 1000).toFixed(1)} s</p>
                      </div>
                    </div>

                    {!synthResult.base_url && (
                      <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                          <strong>LLM_BASE_URL n'est pas défini.</strong> L'appel est parti
                          directement chez le fournisseur, hors passerelle : sans clé virtuelle,
                          sans budget, sans suivi de dépense, et hors du point unique où le
                          registre RGPD décrit le sous-traitant.
                        </span>
                      </div>
                    )}

                    <div className="border-t pt-4">
                      <SynthesisView synthesis={synthResult.synthesis} />
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* --- Meeting bot --- */}
            <TabsContent value="bot" className="mt-4 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Bot className="h-4 w-4" /> Bot de réunion
                  </CardTitle>
                  <CardDescription>
                    Envoie un bot de test sur une URL de réunion arbitraire (Google Meet, Teams,
                    Zoom…) pour vérifier qu'il rejoint, enregistre, et remonte un statut
                    exploitable. Aucun candidat ni campagne n'est impliqué.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="meeting-url">URL de la réunion</Label>
                      <Input
                        id="meeting-url"
                        placeholder="https://meet.google.com/xxx-xxxx-xxx"
                        value={meetingUrl}
                        onChange={(e) => setMeetingUrl(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !launching) handleLaunchBot();
                        }}
                        disabled={launching || (!!bot && !isTerminal)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="bot-name">Nom du bot (optionnel)</Label>
                      <Input
                        id="bot-name"
                        placeholder="RecrutAuto Test Bot"
                        value={botName}
                        onChange={(e) => setBotName(e.target.value)}
                        disabled={launching || (!!bot && !isTerminal)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="test-skills">
                        Compétences de test (séparées par des virgules)
                      </Label>
                      <Input
                        id="test-skills"
                        placeholder="Python, Kubernetes, Databricks"
                        value={testSkills}
                        onChange={(e) => setTestSkills(e.target.value)}
                      />
                    </div>
                  </div>

                  {launchError && (
                    <div className="px-3 py-2 rounded text-sm bg-red-50 text-red-800 border border-red-200">
                      {launchError}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleLaunchBot}
                      disabled={!meetingUrl.trim() || launching || (!!bot && !isTerminal)}
                    >
                      {launching ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      {launching ? "Envoi..." : "Envoyer le bot"}
                    </Button>
                    {bot && isTerminal && (
                      <Button type="button" variant="outline" onClick={handleResetBot}>
                        <RefreshCw className="h-4 w-4 mr-2" /> Nouveau test
                      </Button>
                    )}
                  </div>

                  {bot && (
                    <div className="space-y-3 border rounded-md p-4">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="text-muted-foreground">Bot :</span>
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{bot.bot_id}</code>
                        <BotStatusBadge status={mappedStatus} />
                        {botStatus?.state && (
                          <span className="text-xs text-muted-foreground font-mono">
                            brut : {botStatus.state}
                          </span>
                        )}
                        {botStatus?.recording_available && (
                          <Badge variant="outline" className="gap-1">
                            <FileAudio className="h-3 w-3" /> Enregistrement disponible
                          </Badge>
                        )}
                        {pollingEnabled && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" /> Actualisation auto (2s)
                          </span>
                        )}
                      </div>

                      {statusError && (
                        <div className="px-3 py-2 rounded text-sm bg-red-50 text-red-800 border border-red-200">
                          {statusError}
                        </div>
                      )}
                      {cancelError && (
                        <div className="px-3 py-2 rounded text-sm bg-red-50 text-red-800 border border-red-200">
                          {cancelError}
                        </div>
                      )}

                      {!isTerminal && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleCancelBot}
                          disabled={cancelling}
                        >
                          {cancelling ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5 mr-2" />
                          )}
                          {cancelling ? "Arrêt..." : "Arrêter le bot"}
                        </Button>
                      )}

                      <div className="space-y-2 border-t pt-3">
                        <Label className="text-sm">Transcript en direct</Label>
                        {!botStatus?.transcript || botStatus.transcript.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            {isTerminal
                              ? "Aucun transcript (bot non admis, silence, ou provider live non configuré — MEETING_BOT_KYUTAI_SERVER_URL)."
                              : "En attente des premières phrases…"}
                          </p>
                        ) : (
                          <div className="max-h-80 overflow-y-auto rounded-md border bg-muted/40 p-3 space-y-2">
                            {botStatus.transcript.map((u, i) => (
                              <p key={i} className="text-xs">
                                <span className="font-semibold">{u.speaker_name}</span>
                                {" : "}
                                <span className="font-mono text-muted-foreground">
                                  {renderTextWithEntities(u.text, u.entities)}
                                </span>
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
